// After a deploy, old hashed chunk files no longer exist on the CDN. The SPA
// fallback used to serve index.html for those URLs, so stale tabs throw
// ChunkLoadError / "Unexpected token '<'" when lazily navigating. Auto-reloads
// with a cache-busting URL pick up fresh index.html + valid chunk URLs.
export const CHUNK_RELOAD_STORAGE_KEY = "ll-chunk-error-reloaded-at";
export const CHUNK_RELOAD_COUNT_STORAGE_KEY = "ll-chunk-error-reload-count";
export const CHUNK_RELOAD_MAX_AUTO_ATTEMPTS = 2;
export const CHUNK_RELOAD_WINDOW_MS = 60 * 1000;
/** @deprecated Use CHUNK_RELOAD_WINDOW_MS — kept for existing imports/tests. */
export const CHUNK_RELOAD_MIN_INTERVAL_MS = CHUNK_RELOAD_WINDOW_MS;
export const CHUNK_RELOAD_RETRY_DELAY_MS = 750;
export const CHUNK_RELOAD_CACHE_BUST_PARAM = "_llcb";

export const STATIC_CHUNK_FILE_PATTERN = /\/static\/(js|css)\/[^/]*chunk[^/]*$/i;

export const isChunkLoadError = (error) => {
  const message = String(error?.message || error || "");
  return (
    error?.name === "ChunkLoadError"
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
    || message.includes("Unexpected token '<'")
  );
};

export const isStaleChunkSyntaxError = (message, filename) => {
  if (!String(message || "").includes("Unexpected token '<'")) {
    return false;
  }
  return STATIC_CHUNK_FILE_PATTERN.test(String(filename || ""));
};

const getReloadState = () => {
  try {
    const lastAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) || 0;
    const attempts = Number(window.sessionStorage.getItem(CHUNK_RELOAD_COUNT_STORAGE_KEY)) || 0;
    if (!lastAt || Date.now() - lastAt >= CHUNK_RELOAD_WINDOW_MS) {
      return { lastAt: 0, attempts: 0 };
    }
    return { lastAt, attempts };
  } catch (storageError) {
    return { lastAt: 0, attempts: 0 };
  }
};

const markChunkReload = (attempts) => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    window.sessionStorage.setItem(CHUNK_RELOAD_COUNT_STORAGE_KEY, String(attempts));
  } catch (storageError) {
    // Ignore storage failures (private mode, etc.); worst case we show the manual UI.
  }
};

/**
 * Hard-navigates to the same URL with a cache-bust query param so the browser
 * cannot reuse a stale index.html / failed chunk response from soft reload.
 */
export const reloadWithCacheBust = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CHUNK_RELOAD_CACHE_BUST_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch (navigationError) {
    window.location.reload();
  }
};

/**
 * Reloads up to CHUNK_RELOAD_MAX_AUTO_ATTEMPTS times per throttle window when a
 * stale chunk is detected. The second attempt is delayed briefly for flaky networks.
 * @returns {"reloaded"|"throttled"|"skipped"}
 */
export const tryReloadForStaleChunk = () => {
  if (typeof window === "undefined") {
    return "skipped";
  }

  const { attempts } = getReloadState();
  if (attempts >= CHUNK_RELOAD_MAX_AUTO_ATTEMPTS) {
    return "throttled";
  }

  const nextAttempt = attempts + 1;
  markChunkReload(nextAttempt);

  const navigate = () => reloadWithCacheBust();
  if (nextAttempt > 1) {
    window.setTimeout(navigate, CHUNK_RELOAD_RETRY_DELAY_MS);
  } else {
    navigate();
  }
  return "reloaded";
};

let staleChunkReloadHandlerInstalled = false;

/**
 * Catches script-parse SyntaxErrors that fire on window.onerror (outside React
 * error boundaries) when a stale chunk URL is served as HTML.
 */
export const installStaleChunkReloadHandler = () => {
  if (typeof window === "undefined" || staleChunkReloadHandlerInstalled) {
    return;
  }
  staleChunkReloadHandlerInstalled = true;

  window.addEventListener("error", (event) => {
    if (!isStaleChunkSyntaxError(event?.message, event?.filename)) {
      return;
    }
    tryReloadForStaleChunk();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event?.reason)) {
      return;
    }
    tryReloadForStaleChunk();
  });
};
